import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText,
  Lock,
  Briefcase,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Calendar,
  StickyNote
} from 'lucide-react';

interface Workspace {
  id: string;
  name: string;
  description?: string;
  owner_org_id: string;
  owner_org_name: string;
  owner_org_subscription?: string;
  has_subscription?: boolean;
  created_at: string;
  updated_at: string;
  bill_count?: number;
  can_manage?: boolean;
  logo_file?: string;
  logo_url?: string;
  unread_notes_count?: number;
}

interface Organization {
  id: string;
  role?: string;
}

interface MobileWorkspaceCardProps {
  workspace: Workspace;
  onWorkspaceClick: (workspace: Workspace) => void;
  organizations: Organization[];
}

const MobileWorkspaceCard: React.FC<MobileWorkspaceCardProps> = ({
  workspace
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hasSubscription = workspace.has_subscription !== false;
  const isLocked = !hasSubscription;
  const hasNewNotes = (workspace.unread_notes_count ?? 0) > 0;

  const handleCardClick = () => {
    if (!isLocked) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleMenuItemClick = (section: string) => {
    navigate(`/workspaces/${workspace.id}?section=${section}`);
  };

  // Menu items for the dropdown (only Bill Tracking, Notes, Calendar)
  // Tab IDs must match workspaceTabs.ts: bills, notes, calendar
  const menuItems = [
    { id: 'bills', label: 'Bill Tracking', icon: FileText },
    { id: 'notes', label: 'Notes', icon: StickyNote },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
  ];

  return (
    <div className="card overflow-hidden">
      {/* Main Card Button */}
      <button
        onClick={handleCardClick}
        disabled={isLocked}
        className={`
          w-full p-3 text-left relative
          ${isLocked ? 'opacity-60' : 'active:bg-background-tertiary dark:active:bg-background-dark-tertiary'}
          transition-all duration-150
        `}
      >
        {/* New Notes Badge */}
        {hasNewNotes && !isLocked && (
          <div className="absolute top-2 right-10 bg-primary-600 dark:bg-primary-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
            {workspace.unread_notes_count}
          </div>
        )}

        {/* Single Row Layout */}
        <div className="flex items-center gap-3">
          {/* Logo */}
          {workspace.logo_url ? (
            <img
              src={workspace.logo_url}
              alt=""
              className="w-12 h-12 rounded object-cover flex-shrink-0 bg-background-tertiary dark:bg-background-dark-tertiary"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-12 h-12 rounded bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0 pr-8">
            {/* Workspace Name */}
            <div className="font-semibold text-base text-text-primary dark:text-text-dark-primary truncate mb-1">
              {workspace.name}
            </div>
            
            {/* Organization */}
            <div className="text-xs text-text-secondary dark:text-text-dark-secondary truncate mb-1">
              {workspace.owner_org_name}
            </div>

            {/* Stats Row */}
            <div className="flex items-center gap-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              {/* Bills Count */}
              {workspace.bill_count !== undefined && workspace.bill_count > 0 && (
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <FileText className="w-3 h-3" />
                  {workspace.bill_count} {workspace.bill_count === 1 ? 'bill' : 'bills'}
                </span>
              )}

              {/* New Notes Indicator */}
              {hasNewNotes && (
                <span className="flex items-center gap-1 whitespace-nowrap text-primary-600 dark:text-primary-400 font-medium">
                  <MessageSquare className="w-3 h-3" />
                  {workspace.unread_notes_count} new
                </span>
              )}
            </div>
          </div>

          {/* Expand/Lock Icon */}
          <div className="flex-shrink-0 absolute right-3 top-1/2 -translate-y-1/2">
            {isLocked ? (
              <Lock className="w-5 h-5 text-error-500 dark:text-error-400" />
            ) : (
              isExpanded ? (
                <ChevronUp className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
              )
            )}
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isExpanded && !isLocked && (
        <div className="border-t border-border-light dark:border-border-dark bg-black/10 dark:bg-black/20">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item.id)}
                className="w-full flex items-center gap-3 pl-10 pr-4 py-3 text-left
                  hover:bg-black/10 dark:hover:bg-black/20
                  active:bg-black/15 dark:active:bg-black/30
                  transition-colors border-b border-border-light/50 dark:border-border-dark/50 last:border-b-0"
              >
                <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileWorkspaceCard;
